import {
  useCallback,
  useMemo,
  useRef,
  useState,
  forwardRef,
  ChangeEvent,
  ReactNode,
  DragEvent,
  useEffect,
  memo,
} from 'react';
import { Box, CircularProgress, Grid2, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FlexCenHorVer, FlexCenVer, FlexCenVerSpaceBetween, FlexCol } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { color, EL_COLORS } from '@/colors';
import { StepKeys } from '@/components/ProductTours/tour-keys';
import { useTour } from '@/components/ProductTours/TourContext';
import { Input } from '@/UI/Input/Input';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { XIcon } from '@/UI/Icons/XIcon';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { ModelItem } from '@/state/nodes/nodes.types';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { Tag } from '@/UI/Tag/Tag';
import { useWorkflowStore } from '@/state/workflow.state';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_SUBFLOWS_SAVED_NODES } from '@/consts/featureFlags';
import { IMPORT_NODE_DEFINITION_ID } from '@/consts/node-types.consts';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { PencilLineIcon } from '@/UI/Icons/PencilLineIcon';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { WorkflowDesignAppSwitch } from './WorkflowDesignAppSwitch/WorkflowDesignAppSwitch';
import { LeftPanelMenuItem } from './LeftPanelSections/LeftPanelMenuItem';
import { MediaLibrarySection } from './LeftPanelSections/MediaLibrarySection';
import { SavedSection, ComingSoonSection } from './LeftPanelSections/SavedSection';
import { InputOutputFilter } from './LeftPanelSections/InputOutputFilter';
import { Section } from './LeftPanelSections/Section';
import { FiltersMenu } from './LeftPanelSections/FiltersMenu';
import { MenuSection } from './LeftToolMenuV2';
import type { MediaAsset } from '@/types/api/assets';

const FLUX_FAST_ID = 'zE9WAgeGgEYnZmIspEGn';

interface CustomTabPanelProps {
  id: MenuSection;
  title: string;
  mt?: number;
  action?: ReactNode;
  children: ReactNode;
}

const CustomTabPanel = forwardRef<HTMLDivElement, CustomTabPanelProps>(
  ({ id, title, children, mt, action, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        role="tabpanel"
        data-testid={`simple-tabpanel-${id}`}
        aria-labelledby={`simple-tab-${id}`}
        mt={mt || 0}
        {...props}
      >
        <FlexCenVerSpaceBetween sx={{ mb: 2, minHeight: action ? 30 : undefined }}>
          <Typography component="div" variant="body-lg-md">
            {title}
          </Typography>
          {action}
        </FlexCenVerSpaceBetween>
        <Box>{children}</Box>
      </Box>
    );
  },
);
CustomTabPanel.displayName = 'CustomTabPanel';

interface LeftPanelProps {
  selectedMenu: MenuSection;
  shouldHidePanel: boolean;
  mediaArray: any[];
  setSelectedItem: (item: MenuSection) => void;
  lastMenuChangeByUser: React.MutableRefObject<boolean>;
  justScrolledRef: React.MutableRefObject<boolean>;
}

const sectionIndexMap: Record<MenuSection, number> = {
  search: -2,
  recent: -1,
  assets: 0,
  toolbox: 1,
  image: 2,
  video: 3,
  threedee: 4,
  models: 5,
  files: 6,
};

const LeftPanelV2 = memo(function LeftPanelV2({
  selectedMenu,
  setSelectedItem,
  lastMenuChangeByUser,
  justScrolledRef,
}: LeftPanelProps) {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { getCurrentStepConfig } = useTour();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEditingName = useWorkflowStore((state) => state.isEditingNameOnToolbar);
  const setIsEditingName = useWorkflowStore((state) => state.setIsEditingNameOnToolbar);
  const isOnSearch = useMemo(() => selectedMenu === 'search', [selectedMenu]);
  const { value: ff_isSubflowsSavedNodes } = useFeatureFlag(FF_SUBFLOWS_SAVED_NODES);
  const [isAssetsEditMode, setIsAssetsEditMode] = useState(false);
  const isOnFiles = useMemo(() => selectedMenu === 'files', [selectedMenu]);

  // Reset edit mode when navigating away from assets section
  useEffect(() => {
    if (selectedMenu !== 'assets') {
      setIsAssetsEditMode(false);
    }
  }, [selectedMenu]);

  const { filters, setSearch, setSource, menu, filteredMenuData, isFiltersActive, isMenuDataLoaded } =
    useNodeFiltersStore();

  const { modelBlockingPermissions } = useSubscriptionPermissions();
  const isInRelevantTourStage = useMemo(
    () => getCurrentStepConfig()?.stepId === StepKeys.NAVIGATION_TOUR.ADD_NODES,
    [getCurrentStepConfig],
  );

  useEffect(() => {
    if (!selectedMenu) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }
    if (justScrolledRef.current) {
      justScrolledRef.current = false;
      return;
    }
    const sectionIdx = sectionIndexMap[selectedMenu];
    const sectionRef = sectionRefs.current[sectionIdx];

    if (lastMenuChangeByUser.current || isInRelevantTourStage) {
      lastMenuChangeByUser.current = false;
      if (sectionRef) {
        timeoutRef.current = setTimeout(() => {
          scrollContainerRef?.current?.scrollTo({ top: sectionRef.offsetTop - 16, behavior: 'auto' });
        }, 50);
        return () => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        };
      }
    }
  }, [selectedMenu, justScrolledRef, lastMenuChangeByUser, isInRelevantTourStage]);

  useEffect(() => {
    if (!selectedMenu || isOnSearch || isOnFiles) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const containerTop = container.getBoundingClientRect().top;
        let currentSection: MenuSection | null = null;
        (Object.entries(sectionIndexMap) as [MenuSection, number][]).forEach(([menu, idx]) => {
          const ref = sectionRefs.current[idx];
          if (ref) {
            const rect = ref.getBoundingClientRect();
            if (rect.top - containerTop <= 40) {
              // 40px threshold for header
              currentSection = menu;
            }
          }
        });
        if (currentSection && currentSection !== selectedMenu) {
          lastMenuChangeByUser.current = false;
          justScrolledRef.current = true;
          setSelectedItem(currentSection);
        }
        ticking = false;
      });
    };
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [setSelectedItem, selectedMenu, lastMenuChangeByUser, justScrolledRef, isOnSearch, isOnFiles]);

  const getShouldDisable = useCallback(
    (itemId) => isInRelevantTourStage && itemId !== FLUX_FAST_ID,
    [isInRelevantTourStage],
  );

  const getIsSelected = useCallback(
    (itemId) => isInRelevantTourStage && itemId === FLUX_FAST_ID,
    [isInRelevantTourStage],
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
  };

  const onDragStart = (e: DragEvent<HTMLDivElement>, item) => {
    const itemString = JSON.stringify(item);
    e.dataTransfer.setData('menuItem', itemString);
    e.dataTransfer.effectAllowed = 'move';
    if (e.target instanceof HTMLElement) {
      e.target.classList.add('dragging');
    }
    document.body.style.cursor = 'grabbing';
  };

  const onDragFileStart = (e: DragEvent<HTMLDivElement>, file: MediaAsset) => {
    const item = {
      type: 'import',
      initialData: file,
      id: IMPORT_NODE_DEFINITION_ID,
    };
    const itemString = JSON.stringify(item);
    e.dataTransfer.setData('menuItem', itemString);
    e.dataTransfer.effectAllowed = 'move';
    if (e.target instanceof HTMLElement) {
      e.target.classList.add('dragging');
    }
    document.body.style.cursor = 'grabbing';
  };

  const onDragEnd = (e: DragEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) {
      e.target.classList.remove('dragging');
    }
    document.body.style.cursor = '';
  };

  const renderSearchResults = (results: ModelItem[]) => {
    if (!results?.length) {
      return (
        <Typography variant="body-sm-rg" sx={{ textAlign: 'center', py: 2 }} color={color.White64_T}>
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.SEARCH.EMPTY)}
        </Typography>
      );
    }

    return (
      <Box className="wea-no-scrollbar" sx={{ overflowY: 'scroll', flex: 1, py: 2 }}>
        <Grid2 container spacing={1}>
          {results.map((item, i) => (
            <Grid2 key={`${item.id}-${i}`} size={6}>
              <LeftPanelMenuItem
                item={item}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                disabled={getShouldDisable(item.id)}
              />
            </Grid2>
          ))}
        </Grid2>
      </Box>
    );
  };

  const hasAnyFilter = useMemo(() => {
    return filters.source.length > 0;
  }, [filters.source]);

  const listHeight = useMemo(() => {
    if (isOnFiles) {
      return `calc(100% - 72px)`;
    }
    if (isOnSearch) {
      return `calc(100% - 72px - ${hasAnyFilter ? 125 : 97}px)`;
    }
    return `calc(100% - 72px - 69px)`;
  }, [isOnFiles, isOnSearch, hasAnyFilter]);

  return (
    <Box
      // TODO: cleanup css of #flow-left-panel when merge
      data-testid="flow-left-panel"
      // className={!shouldHidePanel && selectedMenu ? 'slide-left-enter' : 'slide-left-exit'}
      sx={{
        height: '100%',
        background: color.Black92,
        borderRight: `1px solid ${EL_COLORS.BoxBorder}`,
      }}
    >
      <FlexCenHorVer sx={{ height: 72, borderBottom: `1px solid ${EL_COLORS.BoxBorder}` }}>
        <WorkflowDesignAppSwitch
          isPaperBg={false}
          hideDevLabel
          isEditingName={isEditingName}
          setIsEditingName={setIsEditingName}
        />
      </FlexCenHorVer>
      {!isOnFiles && (
        <FlexCol sx={{ py: 2, borderBottom: isOnSearch ? `1px solid ${EL_COLORS.BoxBorder}` : 'none', gap: 1.5 }}>
          <FlexCenVer sx={{ px: 2, gap: 0.5 }}>
            <Input
              sx={{ flex: 1 }}
              size="small"
              value={filters.search}
              placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
              onChange={handleSearchChange}
              startAdornment={<SearchIcon />}
              onFocus={() => setSelectedItem('search')}
              endAdornment={
                filters.search && (
                  <XIcon width={16} height={16} style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />
                )
              }
            />
            {isOnSearch && <FiltersMenu />}
          </FlexCenVer>
          {isOnSearch && (
            <>
              <FlexCenVer sx={{ px: 2 }}>
                <InputOutputFilter />
              </FlexCenVer>

              {hasAnyFilter && (
                <FlexCenVer sx={{ px: 2, gap: 1, flexWrap: 'wrap' }}>
                  {filters.source.map((value) => (
                    <Tag
                      key={value}
                      isClearable
                      text={t(
                        I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.MODE_TOGGLE[
                          value === 'tools' ? 'TOOLS' : 'MODELS'
                        ],
                      )}
                      bgColor={color.White80_T}
                      textColor={color.Black92}
                      variant="large"
                      onClear={() => setSource(filters.source.filter((v) => v !== value))}
                    />
                  ))}
                </FlexCenVer>
              )}
            </>
          )}
        </FlexCol>
      )}
      {isMenuDataLoaded ? (
        <FlexCol sx={{ height: listHeight, px: 2 }}>
          {isFiltersActive ? (
            renderSearchResults(filteredMenuData)
          ) : (
            <Box
              ref={scrollContainerRef}
              sx={{ position: 'relative', flex: 1, overflowY: 'scroll', scrollbarWidth: 'none', py: 2 }}
            >
              {!isOnFiles && (
                <>
                  {menu.recent.children?.length > 0 && (
                    <CustomTabPanel
                      ref={(el: HTMLDivElement | null) => {
                        sectionRefs.current[-1] = el;
                      }}
                      title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.RECENT)}
                      id="recent"
                    >
                      <Section
                        node={menu.recent}
                        onDragEnd={onDragEnd}
                        onDragStart={onDragStart}
                        getShouldDisable={getShouldDisable}
                        getIsSelected={getIsSelected}
                        hideTitle
                      />
                    </CustomTabPanel>
                  )}
                  {/* workspace assets */}
                  {ff_isSubflowsSavedNodes && (
                    <CustomTabPanel
                      ref={(el: HTMLDivElement | null) => {
                        sectionRefs.current[0] = el;
                      }}
                      title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.TITLE)}
                      id="assets"
                      action={
                        isAssetsEditMode ? (
                          <ButtonContained
                            mode="outlined"
                            size="small"
                            onClick={() => setIsAssetsEditMode(false)}
                            sx={{ border: `1px solid ${color.White16_T}` }}
                          >
                            {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.DONE)}
                          </ButtonContained>
                        ) : (
                          <Tooltip title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.EDIT_TOOLTIP)}>
                            <AppIconButton width={24} height={24} onClick={() => setIsAssetsEditMode(true)}>
                              <PencilLineIcon width={16} height={16} />
                            </AppIconButton>
                          </Tooltip>
                        )
                      }
                    >
                      <FlexCol sx={{ gap: 3 }}>
                        <SavedSection
                          node={menu.savedModels}
                          titleKey={I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.MODELS}
                          emptyKey={I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.MODELS_EMPTY}
                          isEditMode={isAssetsEditMode}
                        />
                        <SavedSection
                          node={menu.savedNodes}
                          titleKey={I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.NODES}
                          emptyKey={I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.NODES_EMPTY}
                          isEditMode={isAssetsEditMode}
                        />
                        <ComingSoonSection titleKey={I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.SUBFLOWS} />
                      </FlexCol>
                    </CustomTabPanel>
                  )}
                  {/* toolbox */}
                  <CustomTabPanel
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[1] = el;
                    }}
                    title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.TOOLBOX)}
                    id="toolbox"
                    mt={ff_isSubflowsSavedNodes ? 5 : 0}
                  >
                    <Section
                      node={menu.tools}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                      getShouldDisable={getShouldDisable}
                      getIsSelected={getIsSelected}
                      hideTitle
                    />
                  </CustomTabPanel>
                  {/* image models */}
                  <CustomTabPanel
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[2] = el;
                    }}
                    title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.IMAGE)}
                    id="image"
                    mt={5}
                  >
                    <Section
                      node={menu.imageModels}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                      getShouldDisable={getShouldDisable}
                      getIsSelected={getIsSelected}
                      hideTitle
                    />
                  </CustomTabPanel>
                  {/* video models */}
                  <CustomTabPanel
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[3] = el;
                    }}
                    title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.VIDEO)}
                    id="video"
                    mt={5}
                  >
                    <Section
                      node={menu.videoModels}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                      getShouldDisable={getShouldDisable}
                      getIsSelected={getIsSelected}
                      hideTitle
                    />
                  </CustomTabPanel>
                  {/* 3d */}
                  <CustomTabPanel
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[4] = el;
                    }}
                    title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.THREEDEE)}
                    id="threedee"
                    mt={5}
                  >
                    <Section
                      node={menu.threedModels}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                      getShouldDisable={getShouldDisable}
                      getIsSelected={getIsSelected}
                      hideTitle
                    />
                  </CustomTabPanel>
                  {/* custom models */}
                  {!modelBlockingPermissions.view && (
                    <CustomTabPanel
                      ref={(el: HTMLDivElement | null) => {
                        sectionRefs.current[5] = el;
                      }}
                      title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.MY_MODELS)}
                      id="models"
                      mt={5}
                    >
                      <Section
                        node={menu.customModels}
                        onDragEnd={onDragEnd}
                        onDragStart={onDragStart}
                        getShouldDisable={getShouldDisable}
                        getIsSelected={getIsSelected}
                        shouldDisplayEmptyOutput={true}
                        isLast
                        hideTitle
                      />
                    </CustomTabPanel>
                  )}
                </>
              )}
              {/* media library */}
              {isOnFiles && (
                <CustomTabPanel title={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.TITLE.MEDIA)} id="files">
                  <MediaLibrarySection onDragFileStart={onDragFileStart} onDragEnd={onDragEnd} />
                </CustomTabPanel>
              )}
            </Box>
          )}
        </FlexCol>
      ) : (
        <FlexCenHorVer sx={{ height: '100%' }}>
          <CircularProgress sx={{ color: color.White100 }} size={32} />
        </FlexCenHorVer>
      )}
    </Box>
  );
});

export default LeftPanelV2;
